import React, { useState } from 'react';
import { ChartIcon } from '../components/icons/NavigationIcons';
import { PlatformIcon } from '../components/PlatformIcon';

export const AnalyticsOverview: React.FC = () => {
  const [dateRange, setDateRange] = useState<'7d' | '30d' | '90d' | 'all'>('30d');

  // Mock data - would come from API
  const kpis = {
    totalSpend: 125430,
    totalRevenue: 384250,
    roas: 3.06,
    impressions: 4530000,
    clicks: 125600,
    ctr: 2.77,
    conversions: 8420,
    conversionRate: 6.7,
  };

  const platformBreakdown = [
    { platform: 'meta' as const, spend: 67500, revenue: 215000, roas: 3.19, impressions: 2400000, clicks: 68000 },
    { platform: 'google' as const, spend: 45230, revenue: 132500, roas: 2.93, impressions: 1680000, clicks: 42300 },
    { platform: 'tiktok' as const, spend: 12700, revenue: 36750, roas: 2.89, impressions: 450000, clicks: 15300 },
  ];

  const topCampaigns = [
    { id: 1, name: 'Summer Sale 2026', platform: 'meta' as const, spend: 15200, revenue: 52300, roas: 3.44 },
    { id: 2, name: 'Q1 Product Launch', platform: 'google' as const, spend: 12500, revenue: 39800, roas: 3.18 },
    { id: 3, name: 'Brand Awareness', platform: 'tiktok' as const, spend: 8900, revenue: 24100, roas: 2.71 },
    { id: 4, name: 'Holiday Special', platform: 'meta' as const, spend: 11300, revenue: 31400, roas: 2.78 },
    { id: 5, name: 'Spring Collection', platform: 'google' as const, spend: 9800, revenue: 26900, roas: 2.75 },
  ];

  const formatCurrency = (num: number) => `$${(num / 1000).toFixed(1)}k`;
  const formatNumber = (num: number) => new Intl.NumberFormat().format(num);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Analytics Overview</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">
            Cross-platform performance metrics and insights
          </p>
        </div>

        {/* Date Range Selector */}
        <div className="flex gap-2">
          {(['7d', '30d', '90d', 'all'] as const).map((range) => (
            <button
              key={range}
              onClick={() => setDateRange(range)}
              className={`
                px-4 py-2 rounded-lg text-sm font-medium transition-colors
                ${
                  dateRange === range
                    ? 'bg-gp-sungold text-white'
                    : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                }
              `}
            >
              {range === 'all' ? 'All Time' : `Last ${range.replace('d', ' Days')}`}
            </button>
          ))}
        </div>
      </div>

      {/* Top KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Total Spend</p>
              <p className="text-3xl font-bold text-gray-900 dark:text-white mt-1">
                {formatCurrency(kpis.totalSpend)}
              </p>
              <p className="text-xs text-green-600 dark:text-green-400 mt-1">↓ 12% vs last period</p>
            </div>
            <div className="w-12 h-12 rounded-full bg-gp-orange-100 dark:bg-gp-orange-900 flex items-center justify-center">
              <ChartIcon className="w-6 h-6 text-gp-orange" />
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Total Revenue</p>
              <p className="text-3xl font-bold text-gp-forest dark:text-gp-forest-400 mt-1">
                {formatCurrency(kpis.totalRevenue)}
              </p>
              <p className="text-xs text-green-600 dark:text-green-400 mt-1">↑ 24% vs last period</p>
            </div>
            <div className="w-12 h-12 rounded-full bg-gp-forest-100 dark:bg-gp-forest-900 flex items-center justify-center">
              <ChartIcon className="w-6 h-6 text-gp-forest" />
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Average ROAS</p>
              <p className="text-3xl font-bold text-gp-sungold dark:text-gp-sungold-400 mt-1">
                {kpis.roas.toFixed(2)}x
              </p>
              <p className="text-xs text-green-600 dark:text-green-400 mt-1">↑ 18% vs last period</p>
            </div>
            <div className="w-12 h-12 rounded-full bg-gp-sungold-100 dark:bg-gp-sungold-900 flex items-center justify-center">
              <ChartIcon className="w-6 h-6 text-gp-sungold" />
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Conversions</p>
              <p className="text-3xl font-bold text-gray-900 dark:text-white mt-1">
                {formatNumber(kpis.conversions)}
              </p>
              <p className="text-xs text-green-600 dark:text-green-400 mt-1">↑ 32% vs last period</p>
            </div>
            <div className="w-12 h-12 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
              <ChartIcon className="w-6 h-6 text-gray-600 dark:text-gray-400" />
            </div>
          </div>
        </div>
      </div>

      {/* Secondary KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
          <p className="text-sm text-gray-600 dark:text-gray-400">Impressions</p>
          <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
            {(kpis.impressions / 1000000).toFixed(2)}M
          </p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
          <p className="text-sm text-gray-600 dark:text-gray-400">Clicks</p>
          <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
            {formatNumber(kpis.clicks)}
          </p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
          <p className="text-sm text-gray-600 dark:text-gray-400">CTR</p>
          <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">{kpis.ctr}%</p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
          <p className="text-sm text-gray-600 dark:text-gray-400">Conv. Rate</p>
          <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
            {kpis.conversionRate}%
          </p>
        </div>
      </div>

      {/* Platform Breakdown */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">Platform Breakdown</h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Performance metrics by advertising platform
          </p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 dark:bg-gray-900">
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Platform
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Spend
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Revenue
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  ROAS
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Impressions
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Clicks
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {platformBreakdown.map((platform) => (
                <tr key={platform.platform} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <PlatformIcon platform={platform.platform} />
                      <span className="ml-3 text-sm font-medium text-gray-900 dark:text-white capitalize">
                        {platform.platform}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-900 dark:text-white">
                    {formatCurrency(platform.spend)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gp-forest dark:text-gp-forest-400 font-medium">
                    {formatCurrency(platform.revenue)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <span
                      className={
                        platform.roas >= 3
                          ? 'text-green-600 dark:text-green-400'
                          : platform.roas >= 2
                          ? 'text-yellow-600 dark:text-yellow-400'
                          : 'text-red-600 dark:text-red-400'
                      }
                    >
                      {platform.roas.toFixed(2)}x
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-600 dark:text-gray-400">
                    {(platform.impressions / 1000000).toFixed(2)}M
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-600 dark:text-gray-400">
                    {formatNumber(platform.clicks)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Top Campaigns */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">Top Performing Campaigns</h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Best campaigns by ROAS in the selected period
          </p>
        </div>
        <div className="divide-y divide-gray-200 dark:divide-gray-700">
          {topCampaigns.map((campaign, index) => (
            <div
              key={campaign.id}
              className="p-6 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4 flex-1">
                  <div className="flex-shrink-0">
                    <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-gp-sungold-100 dark:bg-gp-sungold-900 text-gp-sungold font-semibold text-sm">
                      #{index + 1}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <PlatformIcon platform={campaign.platform} />
                      <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                        {campaign.name}
                      </p>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Spend: {formatCurrency(campaign.spend)} • Revenue:{' '}
                      {formatCurrency(campaign.revenue)}
                    </p>
                  </div>
                </div>
                <div className="ml-4 flex items-center">
                  <span
                    className={`
                      inline-flex items-center px-3 py-1 rounded-full text-sm font-medium
                      ${
                        campaign.roas >= 3
                          ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200'
                          : campaign.roas >= 2
                          ? 'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200'
                          : 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200'
                      }
                    `}
                  >
                    {campaign.roas.toFixed(2)}x ROAS
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AnalyticsOverview;
